import { useContext, useEffect, useState } from "react";
import { Box, Typography, Stack, Button, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import LayoutTemplate from "layout/Page";
import { Context } from "../SettingMenu/context";
import CardSelectLevel from "./CardSelectLevel";
import { ArrowBack, Refresh, Save } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { useDialog } from "context/Dialog";
import { TYPE_COMPONENT } from ".";
import SelectYellowLevel from "./SelectYellowLevel";
import { CardContentSecondary } from "components/card";

const Card = styled(`div`)`
  padding: 20px;
  background: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  width: inherit;
  box-sizing: border-box;
`;

const StackCardSelect = styled(Stack)`
  .Box-card:first-child {
    margin-left: auto;
  }
  .Box-card:last-child {
    margin-right: auto;
  }
`;

const SettingLevelTemplate = ({
  config = {
    type: "",
    title: "",
    levelTitle: "",
    content: "",
    listLevel: [],
    typeComponent: TYPE_COMPONENT.LEVEL,
    descriptionUnderTheImage: "",
  },
  handleBack,
  isAllowEdit = false,
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const history = useHistory();
  const {
    listTypeLevelSetting,
    getLevelSettingByType,
    handleReset,
    handleSaveByType,
    loadingUpdateUserProfile,
  } = useContext(Context);
  const [levelIdSelect, setIdLevel] = useState(null);
  const [optionSelect, setOptionSelect] = useState({
    isAuto: false,
  });
  const { getWantBackDialog } = useDialog();

  const breadcrumbs = [
    {
      text: t("page.first"),
      handleChangeTo: () => {
        if (getLevelSettingByType(config.type).id === levelIdSelect) {
          history.push("/mainmenu");
        } else {
          getWantBackDialog({
            onCancel: async () => {
              history.push("/mainmenu");
            },
          });
        }
      },
    },
    {
      text: t("page.setting"),
      handleChangeTo: () => {
        if (getLevelSettingByType(config.type).id === levelIdSelect) {
          history.push("/setting");
        } else {
          getWantBackDialog({
            onCancel: async () => {
              history.push("/setting");
            },
          });
        }
      },
    },
    {
      text: t("page.SettingLevel"),
    },
  ];

  useEffect(() => {
    if (listTypeLevelSetting) {
      setIdLevel(getLevelSettingByType(config.type).id);
      setOptionSelect(getLevelSettingByType(config.type)?.option);
    }
  }, [listTypeLevelSetting]);

  const filterLevelNotUsed = (i) => !(config.type === "chalky" && [0, 28].includes(i.id));
  return (
    <LayoutTemplate BreadcrumbsItems={breadcrumbs}>
      <Typography variant="h4" textAlign="center">
        {config.title}
      </Typography>
      {listTypeLevelSetting && (
        <Container maxWidth="md" disableGutters sx={{ mt: 3 }}>
          <Card>
            <CardContentSecondary>
              <Typography variant="h6">
                {t("settingMenu.inspectionSetting")} : {config.levelTitle}
              </Typography>
            </CardContentSecondary>
            <Typography variant="body1" sx={{ color: "text.secondary", mt: "12px" }}>
              {config.content}
            </Typography>
          </Card>
          <Card sx={{ mt: "12px" }}>
            {config.typeComponent === TYPE_COMPONENT.LEVEL && (
              <>
                <CardContentSecondary>
                  <Typography variant="h6">{t("settingLevel.select_level")}</Typography>
                </CardContentSecondary>
                <StackCardSelect
                  direction={"row"}
                  spacing={1}
                  sx={{ mt: "24px", pb: "12px", mb: "12px", overflowX: "auto" }}
                >
                  {config.listLevel.filter(filterLevelNotUsed).map(({ image, level, id }) => {
                    return (
                      <Stack key={id} className="Box-card" alignItems="center" spacing={2}>
                        <Box onClick={() => isAllowEdit && setIdLevel(id)}>
                          <CardSelectLevel
                            image={image.normal}
                            imageHover={image.highlight}
                            title={t("settingLevel.level", { level: level })}
                            selectLevel={id === levelIdSelect}
                            highlight={id >= levelIdSelect}
                            width={197}
                            height={224}
                          />
                        </Box>
                        <LineSelectLevel select={id >= levelIdSelect} />
                      </Stack>
                    );
                  })}
                </StackCardSelect>
                <div style={{ textAlign: "center" }}>
                  <Typography variant="body3" sx={{ color: "text.secondary" }}>
                    {config.descriptionUnderTheImage}
                  </Typography>
                </div>
              </>
            )}
            {config.typeComponent === TYPE_COMPONENT.LEVEL_YELLOW && (
              <SelectYellowLevel
                onChange={(id) => isAllowEdit && setIdLevel(id)}
                value={levelIdSelect}
                autoYellow={optionSelect?.isAuto}
                onAutoYellow={(checked) => setOptionSelect({ isAuto: checked })}
                isAllowEdit={isAllowEdit}
              />
            )}
          </Card>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "flex-start", sm: "space-between" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mt: "20px" }}
            spacing={3}
          >
            <Button
              id="button-back"
              variant="outlined"
              color="primary"
              sx={{ minWidth: "150px", fontSize: "17px", height: "42px" }}
              startIcon={<ArrowBack />}
              onClick={() => handleBack(getLevelSettingByType(config.type).id === levelIdSelect)}
            >
              {t("back")}
            </Button>
            {isAllowEdit && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Button
                  variant="text"
                  sx={{ color: "primary.light01", textDecoration: "underline" }}
                  startIcon={<Refresh />}
                  onClick={() => handleReset(config.type)}
                >
                  {t("default")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  sx={{ minWidth: "150px", fontSize: "17px", height: "42px" }}
                  onClick={() => handleSaveByType(config.type, levelIdSelect, optionSelect)}
                  disabled={loadingUpdateUserProfile}
                >
                  {t("save")}
                </Button>
              </Stack>
            )}
          </Stack>
        </Container>
      )}
    </LayoutTemplate>
  );
};

export default SettingLevelTemplate;

export const LineSelectLevel = ({ select = false }) => {
  return (
    <Box
      sx={{
        background: select ? "#4B9752" : "#909090",
        border: `3px solid ${select ? "#4B9752" : "#909090"}`,
        width: "80px",
        borderRadius: 1,
      }}
    />
  );
};
